import { FC, ReactNode } from "react";

import { Box, Button, ModelIcon, Row, Text } from "@hightouch/htui";

import { IntegrationIcon } from "src/components/integrations/integration-icon";

/**
 * Shared table primitives for the Liquid template tester — the "sample data →
 * result" preview used by both the Test and Migrate modes. Keeping these in one
 * place means the two modes render an identical, sleek table frame.
 */

export function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

/**
 * Bordered, monospace table frame. `scrollX` (default true) makes it
 * horizontally scrollable for wide tables; pass `false` for a narrow table that
 * sits beside a `flex={1}` sibling — `overflow-x: auto` sets `min-width: 0`,
 * which lets the flex sibling collapse this frame to nothing.
 */
export const PreviewTableFrame: FC<{
  children: ReactNode;
  scrollX?: boolean;
}> = ({ children, scrollX = true }) => (
  <Box
    border="1px solid"
    borderColor="base.border"
    borderRadius="md"
    overflowX={scrollX ? "auto" : undefined}
  >
    <Box
      as="table"
      width="100%"
      fontSize="sm"
      fontFamily="mono"
      sx={{ borderCollapse: "collapse" }}
    >
      {children}
    </Box>
  </Box>
);

export const PreviewHeaderCell: FC<{ children: ReactNode; width?: string }> = ({
  children,
  width,
}) => (
  <Box
    as="th"
    width={width}
    px={2}
    py={1.5}
    textAlign="left"
    fontWeight="medium"
    fontSize="xs"
    color="text.secondary"
    whiteSpace="nowrap"
    borderBottom="1px solid"
    borderColor="base.border"
  >
    {children}
  </Box>
);

export type SampleDataHeaderProps = {
  count: number;
  modelName?: string;
  sourceIcon?: string;
  sourceName?: string;
};

/** The "{model} · first N rows" caption above the sample-data table. */
export const SampleDataHeader: FC<SampleDataHeaderProps> = ({
  count,
  modelName,
  sourceIcon,
  sourceName,
}) => (
  <Row gap={1.5} align="center">
    {sourceIcon ? (
      <IntegrationIcon name={sourceName} src={sourceIcon} size={4} />
    ) : (
      modelName && (
        <Box as={ModelIcon} boxSize={4} color="text.tertiary" flexShrink={0} />
      )
    )}
    <Text fontWeight="medium" size="sm" color="text.secondary">
      {modelName ? `${modelName} · first ${count} rows` : "Sample data"}
    </Text>
  </Row>
);

/**
 * The model's sample rows, one column per field. Fills its container width.
 * When `rowLabels` is supplied, a narrow leading column labels each row (used by
 * the migration tool to mark synthetic edge-case probes among the real sample).
 */
export const SampleDataTable: FC<{
  rows: Record<string, unknown>[];
  columns: string[];
  rowLabels?: string[];
}> = ({ rows, columns, rowLabels }) => (
  <PreviewTableFrame>
    <Box as="thead">
      <Box as="tr" bg="base.background">
        {rowLabels && <PreviewHeaderCell width="44px"> </PreviewHeaderCell>}
        {columns.map((col) => (
          <PreviewHeaderCell key={col}>{col}</PreviewHeaderCell>
        ))}
      </Box>
    </Box>
    <Box as="tbody">
      {rows.map((row, i) => (
        <Box as="tr" key={i} borderTop="1px solid" borderColor="base.border">
          {rowLabels && (
            <Box
              as="td"
              px={2}
              py={1}
              whiteSpace="nowrap"
              fontSize="xs"
              fontWeight="medium"
              color="text.secondary"
              borderRight="1px solid"
              borderColor="base.border"
            >
              {rowLabels[i] ?? String(i + 1)}
            </Box>
          )}
          {columns.map((col) => (
            <Box
              as="td"
              key={col}
              px={2}
              py={1}
              whiteSpace="nowrap"
              maxWidth="160px"
              overflow="hidden"
              textOverflow="ellipsis"
              fontSize="xs"
            >
              {formatCellValue(row[col])}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  </PreviewTableFrame>
);

/** Shown while the model's sample rows are loading. */
export const SampleDataLoading: FC = () => (
  <Row
    p={3}
    bg="base.background"
    borderRadius="md"
    border="1px solid"
    borderColor="base.border"
    align="center"
  >
    <Text size="sm" color="text.secondary">
      Loading sample data from model…
    </Text>
  </Row>
);

/** Shown when no sample rows are available, with an optional load action. */
export const NoSampleData: FC<{ onLoadRows?: () => void }> = ({
  onLoadRows,
}) => (
  <Row
    p={3}
    bg="warning.background"
    borderRadius="md"
    border="1px solid"
    borderColor="warning.border"
    align="center"
    justify="space-between"
  >
    <Text size="sm" color="text.secondary">
      No sample data available.
    </Text>
    {onLoadRows && (
      <Button size="sm" variant="secondary" onClick={onLoadRows}>
        Load preview
      </Button>
    )}
  </Row>
);
